import React, { useState, useMemo, useCallback, useEffect } from "react";
import Draggable, { ControlPosition } from "react-draggable";
import { DiagramEngine } from "@projectstorm/react-diagrams";
import * as _ from "lodash";
import clsx from "clsx";
import { Tooltip } from "@material-ui/core";
import PanToolIcon from "@material-ui/icons/PanTool";
import Port from "./Port";
import { MathNearestTenth, getPortName } from "../../../utils/methods";
import { NENodeModel } from "./CustomNode/NENodeModel";
import GlobeIcon from "../../../assets/images/ic-router_big.svg";
import RouterIcon from "../../../assets/images/ic-internet_big.svg";
import CloudIcon from "../../../assets/images/ic-cloud_big.svg";
import NoIcon from "../../../assets/images/ic-default-big.svg";
import BandwidthGroupIcon from "../../../assets/images/bandwidth-group-small.svg";
import { STATUS_TYPE } from "../../../utils/appConstants";
import { adjustBandwidthPosition } from "./DraggableUtil";
import { Icon } from "@iconify/react";
import ContextMenu from "../../DesignSystem/context_menu";
import { MenuItem } from "@mui/material";
import BasicMenu from "../../DesignSystem/menu";
import { useIsMounted } from "../../../utils/customHooks";

interface GroupProps {
  engine: DiagramEngine;
  node: NENodeModel;
  group: any;
  onGroupExpandCollapse?: (_: any) => void;
  groupModel?: any;
  isBandwidth?: boolean;
  onResizeStart?: boolean;
  boundsRef: HTMLDivElement;
  refresh: boolean;
}

const Group: React.FC<GroupProps> = ({ engine, node, group, isBandwidth, onGroupExpandCollapse, boundsRef, refresh }) => {
  const [isExpanded, setExpanded] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [disableDrag, setDisableDrag] = useState(false);
  const [debouncePorts, setDebouncePorts] = useState(false);
  const [expandedPosition, setExpandedPosition] = useState<ControlPosition>({ x: group.x, y: group.y });
  const [listener, setListener] = useState<any>();
  const [closeListener, setCloseListener] = useState<any>();

  const isMounted = useIsMounted();

  useEffect(() => {
    const expandedIds = JSON.parse(localStorage.getItem("expandedGroupIds") || "[]");
    if (expandedIds.includes(group.groupId)) {
      setExpanded(true);
    }
  }, [group]);

  useEffect(() => {
    if (!isExpanded) {
      setExpandedPosition({ x: group.x, y: group.y });
    }
  }, [isExpanded, group.x, group.y]);
  
  // engine.fireEvent({},'expandGroup214')
  useEffect(() => {
    const groupFunction = `expandGroup${group.groupId}`;
    const groupListener: any = {}
    groupListener[groupFunction] = () => {
      if(!isMounted) return;
      setExpanded(true);
      setDebouncePorts(true);
      onGroupExpandCollapse && onGroupExpandCollapse({ groupId: group.groupId, action: "add" });
    }
    const temp = engine.registerListener(groupListener);
    setListener(temp);
    const groupFunctionClose = `collapseGroup${group.groupId}`;
    const groupListenerClose: any = {}
    groupListenerClose[groupFunctionClose] = () => {
      setExpanded(false);
      setDebouncePorts(false);
      onGroupExpandCollapse && onGroupExpandCollapse({ groupId: group.groupId, action: "remove" });
    }
    const tempvar = engine.registerListener(groupListenerClose);
    setCloseListener(tempvar);
    return () => {
      engine.deregisterListener(closeListener);
      engine.deregisterListener(listener);
    }
  }, [])
  
  const expandCollapse = useCallback(() => {
    setExpanded((prevExpanded) => !prevExpanded);
    setDebouncePorts((prev) => !prev);
    if (onGroupExpandCollapse) {
      onGroupExpandCollapse({ groupId: group.groupId, action: isExpanded ? "remove" : "add" });
    }
  }, [isExpanded, group.groupId]);

  const rightClickGroup = useCallback(
    (e: any) => {
      if (e.button === 2) {
        e.preventDefault();
        expandCollapse();
      }
    },
    [expandCollapse]
  );

  const groupIcon = useMemo(() => {
    if (isBandwidth) {
      return BandwidthGroupIcon;
    }
    if (group.icon === "globe") {
      return GlobeIcon;
    }
    if (group.icon === "router") {
      return RouterIcon;
    }
    if (group.icon === "cloud") {
      return CloudIcon;
    }

    return NoIcon;
  }, [group.icon, isBandwidth]);

  const organizationId = useMemo(() => parseInt((localStorage.getItem("org_id") && localStorage.getItem("org_id")) || "0"), []);

  const countPorts = useCallback(() => {
    let sum = 0;
    group.ports.forEach((port: any) => {
      if (
        (port.crossConnectPortId !== null || // PORT IN CROSS CONNECT, NOT NECESSARILY VERIFIED
          port.status === STATUS_TYPE.FOR_SALE_PORT ||
          port.status === STATUS_TYPE.FOR_SALE_BW || // YELLOW OR WHITE PORT
          (port.status === STATUS_TYPE.BOUGHT_PORT && !port.purchasePortId) || // RED PORT
          (port.status === STATUS_TYPE.BOUGHT_BW && port.current_owner_organization_id !== organizationId)) === false
      ) {
        sum++;
      }
    });

    return sum;
  }, [group, organizationId]);

  let tooltipCSS = "tooltip";
  if (isMoving) {
    tooltipCSS = "tooltip hide-tooltip";
  }
  const tooltip = (
    <div className="no-select group-tooltip-title">
      <div>
        <label>{group.groupName}</label>
      </div>
      <div>
        <label>
          {countPorts()} of {group.ports.length} ports available
        </label>
      </div>
      <div>
        <div>
          {/* <img src={ShowDetailsIcon} alt="Expand" onClick={expandCollapse} /> */}
          <span>Right Click to Expand</span>
        </div>
      </div>
    </div>
  );

  const groupContent = useMemo(() => {
    let content;
    if (group && group.ports) {
      let portIndex = 0;
      group.ports.sort(function (a: any, b: any) {
        const nameA = `${a.port_description} ${a.port_name}`;
        const nameB = `${b.port_description} ${b.port_name}`;
        return nameA.localeCompare(nameB);
      });
      content = group.ports.map((port: any, index: number) => {
        const name = getPortName(port.portId);
        const portDetails = node.getPort(name);
        portIndex++;
        const portContent = (
          <li key={portIndex} className={isExpanded || portIndex == 1 ? "" : "nested"}>
            <Port
              key={index}
              port={port}
              engine={engine}
              portModel={portDetails}
              draggable="not-allowed"
              isExpanded={isExpanded}
              debouncePorts={debouncePorts}
              isGroup
              isBandwidth={isBandwidth}
              boundsRef={boundsRef}
            />
          </li>
        );
        return portContent;
      });
    }
    return content;
  }, [group, isExpanded, debouncePorts, boundsRef.clientHeight, boundsRef.clientWidth, boundsRef, isBandwidth, engine]);

  const handleDragStart = useCallback((e: any) => {
    e.stopPropagation(); // To prevent drag effect on canvas so X,Y not move
    setIsMoving(true);
  }, []);

  const handleDrag = useCallback(
    (e: any) => {
      if (isMoving) {
        e.stopPropagation(); // To prevent drag effect
        setDebouncePorts((prev) => !prev);
      }
    },
    [isMoving]
  );

  const handleDragStop = useCallback(
    (e: any, position: any) => {
      if (isMoving) {
        if (!isExpanded) {
          group.x = MathNearestTenth(position.x);
          group.y = MathNearestTenth(position.y);
        } else {
          setExpandedPosition({ x: position.x, y: position.y });
        }
        if (isBandwidth) {
          adjustBandwidthPosition(group, boundsRef);
        }
        e.stopPropagation(); // To prevent drag effect
        engine?.fireEvent({}, "onCanvasMove");
        setIsMoving(false);
      } else {
        setDisableDrag(false);
      }
    },
    [isMoving, isExpanded]
  );

  // Re-update the coords because it does not work.
  React.useEffect(() => {
    setDebouncePorts(!debouncePorts);
  }, [isMoving, boundsRef.clientWidth, boundsRef.clientHeight, refresh]);

  const toolbar = React.useMemo(() => {
    if (!isExpanded) return null;
    return (
      <div className="groupPortToolbar">
        {/* 🔹 Group Name (Left-Aligned, Becomes a Link if Description Exists) */}
        <div className="groupName">
          {group.description ? (
            <a href={group.description} target="_blank" rel="noopener noreferrer">
              {group.groupName}
            </a>
          ) : (
            <span>{group.groupName}</span>
          )}
        </div>
  
        {/* 🔹 Grab Handle (Far Right) */}
        <Tooltip title="Drag to Move Group" arrow placement="top">
          <div className="grabHandle">
            <PanToolIcon className={clsx("dragIcon", isMoving && "grabbed", !isMoving && "grab")} />
          </div>
        </Tooltip>
      </div>
    );
  }, [isExpanded, isMoving, group.groupName, group.description]);
  
  return (
    <Draggable
      defaultPosition={{ x: group.x || 0, y: group.y || 0 }}
      position={!isExpanded ? { x: group.x, y: group.y } : { x: expandedPosition.x, y: expandedPosition.y }}
      cancel=".no-cursor-group"
      bounds={isExpanded ? "" : "parent"}
      handle={isExpanded ? ".dragIcon" : ".group-container"} // Currently, there is no 'handle' for expanded bandwidth, so draggable is disabled
      onStart={handleDragStart}
      onDrag={handleDrag}
      onStop={handleDragStop}
      scale={(engine?.getModel().getZoomLevel() || 100) / 100}
      disabled={disableDrag}
    >
      <div
        onContextMenu={rightClickGroup}
        style={{ display: "inline-block", position: "absolute", top: 0, left: 0 }}
        className={clsx(
          "group-container no-select",
          !isExpanded && "hide",
          isExpanded && "expanded",
          isMoving && "port-zindex",
          !isBandwidth && isExpanded && "internal-expanded",
          isBandwidth && "no-margin"
        )}
      >
        {toolbar}

        <ul
          className={clsx(
            "group-port no-select",
            !isExpanded && "hide",
            isBandwidth && "icon-bandwidth",
            group.icon && `icon-${group.icon}`
          )}
        >
          <ul className="group-port">
            <div className="ports-root" style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
            {groupContent}
            </div>
          </ul>
        </ul>

        {!isExpanded && (
          <Tooltip
            classes={{
              popper: "tooltip-popper",
              tooltip: tooltipCSS,
            }}
            title={tooltip}
            arrow
            placement="bottom"
            disableTouchListener
          >
            <div className={clsx("grp-icon no-select", isBandwidth && "bandwidth", isMoving && "icon-moving")}>
              <img src={groupIcon} alt="Group of Nr" className={clsx("group-icon", isBandwidth && "bandwidth")} />
            </div>
          </Tooltip>
        )}
      </div>
    </Draggable>
  );
};

export default Group;
